import { useMemo, useState } from 'react';
import type { AnnotationCategory, Partie } from '../lib/types';

/**
 * Popup d'annotation flottante (M1 / Sprint 1ter+).
 * Anchorage : ``position: sticky; bottom`` à l'intérieur du scroller PDF
 * pour rester en bas de la zone visible. Click sur une catégorie =
 * sauvegarde immédiate. Pas de champ commentaire dans le popup : les
 * commentaires libres se font dans la « Doc travail » via les notes
 * intercalées (ADR-031).
 */

const CATEGORIES: { value: AnnotationCategory; label: string; bg: string }[] = [
  { value: 'favorable', label: 'Favorable', bg: 'bg-cat-fav-pastel text-cat-fav' },
  { value: 'defavorable', label: 'Défavorable', bg: 'bg-cat-def-pastel text-cat-def' },
  { value: 'important', label: 'Important', bg: 'bg-cat-imp-pastel text-cat-imp-text' },
  { value: 'a_verifier', label: 'À vérifier', bg: 'bg-cat-ver-pastel text-cat-ver' },
];

export type AnnotationPopupSubmit = {
  category: AnnotationCategory;
  partie_ids: string[];
};

type Props = {
  selectedText: string;
  parties?: Partie[];
  onSubmit: (payload: AnnotationPopupSubmit) => Promise<void> | void;
  onCancel: () => void;
};

export default function AnnotationPopup({ selectedText, parties = [], onSubmit, onCancel }: Props) {
  const [submitting, setSubmitting] = useState<AnnotationCategory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkedIds, setLinkedIds] = useState<string[]>([]);
  const [partiesOpen, setPartiesOpen] = useState(false);
  const [partySearch, setPartySearch] = useState('');

  const toggleParty = (id: string) => {
    setLinkedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  // Filtre tolérant accents (recherche par nom OU prénom).
  const stripDiacritics = (s: string): string =>
    s.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const normalized = stripDiacritics(partySearch).toLowerCase().trim();
  const filteredParties = useMemo(() => {
    if (!normalized) return parties;
    return parties.filter((p) => {
      const hay = stripDiacritics(`${p.prenom ?? ''} ${p.nom ?? ''}`).toLowerCase();
      return hay.includes(normalized);
    });
  }, [parties, normalized]);
  const linkedParties = parties.filter((p) => linkedIds.includes(p.id));

  const submitWith = async (category: AnnotationCategory) => {
    if (submitting) return;
    setSubmitting(category);
    setError(null);
    try {
      await onSubmit({ category, partie_ids: linkedIds });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      // 2026-09-10 : remise a zero dans un `finally`, pas dans le `catch`.
      // Avant, une sortie SILENCIEUSE de submitAnnotation (sans exception)
      // laissait `submitting` fige -> les 4 boutons restaient `disabled`
      // pour toujours, sans le moindre message. Panne invisible.
      setSubmitting(null);
    }
  };

  return (
    <div
      className="sticky bottom-4 z-50 mx-auto w-[320px] rounded-md border border-rule-strong bg-paper-elevated p-3 shadow-lg"
      // On stoppe la propagation mousedown ET mouseup pour que le
      // ``onMouseUp`` du scroller parent (PdfEditorPage:1648) ne re-lise pas
      // window.getSelection() après un clic dans le popup. Sinon, cliquer
      // sur "Lier à une partie" ferme le popup parce que la sélection texte
      // peut être vide à ce moment-là (le navigateur clear le range quand
      // on clique en dehors de la zone surlignée).
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-1.5 flex items-start justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[1px] text-text-tertiary">
          Sélection en cours
        </span>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting !== null}
          aria-label="Fermer sans enregistrer"
          className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
        >
          ✕
        </button>
      </div>
      <div className="mb-2.5 line-clamp-3 rounded-sm bg-paper-card px-2.5 py-2 font-serif text-[12px] italic leading-snug text-text-primary">
        « {selectedText} »
      </div>

      {parties.length > 0 && (
        <div className="mb-2.5">
          {/* En-tête repliable. Affiche les chips des parties DÉJÀ liées
              (compact, pour rappel) + un bouton pour dérouler la liste
              complète. Le panneau reste fermé par défaut pour ne pas
              encombrer la sélection (≥ 18 avocats au cabinet + parties
              des dossiers = listes longues). */}
          <button
            type="button"
            onClick={() => setPartiesOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-sm bg-paper-card px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.5px] text-text-tertiary hover:bg-paper-subtle"
            aria-expanded={partiesOpen}
          >
            <span>
              Lier à une partie {linkedIds.length > 0 ? `(${linkedIds.length})` : ''}
            </span>
            <span aria-hidden="true">{partiesOpen ? '▾' : '▸'}</span>
          </button>
          {!partiesOpen && linkedParties.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {linkedParties.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleParty(p.id)}
                  disabled={submitting !== null}
                  className={`rounded-pill border px-2 py-[2px] text-[10px] font-medium ${
                    p.is_client
                      ? 'border-accent bg-accent-light text-accent-text'
                      : 'border-text-primary bg-paper-subtle text-text-primary'
                  }`}
                  title="Cliquer pour délier"
                >
                  {p.prenom ? `${p.prenom[0]!}. ` : ''}
                  {p.nom} ✕
                </button>
              ))}
            </div>
          )}
          {partiesOpen && (
            <div className="mt-1 rounded-sm border border-rule bg-paper-card p-2">
              {parties.length > 5 && (
                <input
                  type="text"
                  value={partySearch}
                  onChange={(e) => setPartySearch(e.target.value)}
                  placeholder="Rechercher une partie…"
                  className="mb-1.5 w-full rounded-sm border border-rule bg-paper-elevated px-2 py-1 text-[11px] text-text-primary outline-none focus:border-accent"
                  autoFocus
                />
              )}
              <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
                {filteredParties.map((p) => {
                  const active = linkedIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleParty(p.id)}
                      disabled={submitting !== null}
                      className={`rounded-pill border px-2 py-[2px] text-[10px] font-medium transition ${
                        active
                          ? p.is_client
                            ? 'border-accent bg-accent-light text-accent-text'
                            : 'border-text-primary bg-paper-subtle text-text-primary'
                          : 'border-rule-strong bg-paper-elevated text-text-tertiary hover:bg-paper-subtle'
                      }`}
                      title={p.is_client ? 'client du cabinet' : p.role}
                    >
                      {p.prenom ? `${p.prenom[0]!}. ` : ''}
                      {p.nom}
                    </button>
                  );
                })}
                {filteredParties.length === 0 && (
                  <span className="text-[10px] italic text-text-tertiary">
                    aucune partie ne correspond
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-1.5">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => void submitWith(c.value)}
            disabled={submitting !== null}
            className={`rounded-md border-[1.5px] border-transparent px-2.5 py-[7px] text-[12px] font-medium transition hover:border-current disabled:opacity-50 ${c.bg}`}
          >
            {submitting === c.value ? '…' : c.label}
          </button>
        ))}
      </div>

      {error && <p className="mt-1.5 text-[11px] text-cat-def">{error}</p>}
      <p className="mt-1.5 text-center text-[10px] text-text-tertiary">
        Choisis une catégorie pour enregistrer
      </p>
    </div>
  );
}
